import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MapPin, Award, ShieldCheck, Wrench, ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/rapido/aterforsaljare";

const FAQ = [
  {
    q: "Är AK Husbil & Fritid officiell Rapido-återförsäljare?",
    a: "Ja. Vi är officiell Rapido-återförsäljare för norra Sverige, med försäljning och service i Kalix. Vi säljer nya Rapido direkt från fabriken i Frankrike och kontrollerade begagnade från hela Europa.",
  },
  {
    q: "Var finns er Rapido-butik?",
    a: "Stabsvägen 5, 952 51 Kalix. Vi öppnar enligt överenskommelse — ring 070-862 97 43 så säkerställer vi att din modell finns på plats när du kommer.",
  },
  {
    q: "Vilken service kan ni utföra på Rapido?",
    a: "Fukttest, tätningsservice, gasol-/värmesystem (Truma & Alde), elarbeten inklusive litiuminstallation, chassiservice (Fiat/Citroën), garantihantering. Vi känner Rapidos konstruktion i detalj.",
  },
  {
    q: "Kan jag beställa en ny Rapido efter mina specifikationer?",
    a: "Absolut. Vi hjälper dig genom hela specifikationsprocessen — chassi, motor, växellåda, färg, utrustningspaket, individuella tillval. Leveranstid från beställning brukar vara 6–10 månader.",
  },
  {
    q: "Har ni verkstadsservice även för Rapido köpta någon annanstans?",
    a: "Ja. Vi servar Rapido oavsett var du köpt den. Boka i god tid — särskilt inför säsongen på våren och i samband med vinterförvaring på hösten.",
  },
];

export const Route = createFileRoute("/rapido/aterforsaljare")({
  head: () => ({
    meta: [
      { title: buildTitle("Rapido återförsäljare i Sverige") },
      {
        name: "description",
        content:
          "Officiell Rapido-återförsäljare i norra Sverige. Nya & begagnade husbilar, verkstad, garantihantering. Stabsvägen 5, Kalix. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "rapido återförsäljare, rapido sverige, rapido kalix, rapido norrland, rapido verkstad, rapido service" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Rapido återförsäljare – AK Husbil & Fritid" },
      { property: "og:description", content: "Officiell Rapido-återförsäljare i norra Sverige. Försäljning, service och garantihantering." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: URL_SELF,
          brand: { "@type": "Brand", name: "Rapido" },
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 51",
            addressCountry: "SE",
          },
          telephone: "+46708629743",
          email: "info@akhusbilfritid.se",
          areaServed: "SE",
          slogan: "Officiell Rapido-återförsäljare i norra Sverige",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: RapidoAterforsaljare,
});

function RapidoAterforsaljare() {
  return (
    <>
      <PageHeader
        label="Officiell Rapido-återförsäljare · Kalix"
        title={<>Din <span className="font-italic-display text-accent">Rapido</span>-återförsäljare i Sverige.</>}
        sub="Vi säljer, servar och lever Rapido. Från beställning av en helt ny husbil från fabriken i La Chartre-sur-le-Loir till fukttest på en 12 år gammal 866F — hela vägen. Familjeägda sedan 2004, i Kalix."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <MapPin size={16} /> Boka besök i Kalix
          </Link>
          <Link to="/rapido/begagnade" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Se begagnade Rapido <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Award, t: "Auktoriserad återförsäljare", d: "Vi säljer nya Rapido direkt från fabrik. Fabriksgaranti, originalreservdelar, korrekt bygggaranti." },
            { icon: Wrench, t: "Verkstad med märkeskunskap", d: "Vi känner Truma, Alde, Thetford, Dometic — och vet var Rapido brukar behöva extra uppmärksamhet." },
            { icon: ShieldCheck, t: "Familjeföretag sedan 2004", d: "Du pratar med samma personer varje gång. Inga försäljningskvoter — vi vill bygga en kundrelation som håller." },
          ].map((c) => (
            <div key={c.t} className="rounded-3xl border border-border bg-card p-8">
              <c.icon className="text-accent" size={28} />
              <h3 className="mt-5 font-display text-2xl">{c.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Kontakt</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl">Kom förbi <span className="font-italic-display text-accent">Kalix</span>.</h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Vi ligger mitt i norra Sverige — enkelt att nå från Luleå, Piteå, Boden, Haparanda, Överkalix och Kiruna. Många kunder kombinerar besöket med en helg i skärgården.
              </p>
              <dl className="mt-8 space-y-4">
                <div className="flex justify-between border-b border-border pb-3">
                  <dt className="text-muted-foreground">Adress</dt>
                  <dd className="font-medium">Stabsvägen 5, 952 51 Kalix</dd>
                </div>
                <div className="flex justify-between border-b border-border pb-3">
                  <dt className="text-muted-foreground">Telefon</dt>
                  <dd className="font-medium">070-862 97 43</dd>
                </div>
                <div className="flex justify-between border-b border-border pb-3">
                  <dt className="text-muted-foreground">E-post</dt>
                  <dd className="font-medium">info@akhusbilfritid.se</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Öppet</dt>
                  <dd className="font-medium">Enligt överenskommelse</dd>
                </div>
              </dl>
            </div>

            <div>
              <p className="eyebrow">Frågor</p>
              <h2 className="mt-3 font-display text-4xl">Vanliga frågor.</h2>
              <div className="mt-8 space-y-5">
                {FAQ.map((f) => (
                  <details key={f.q} className="rounded-2xl border border-border bg-card p-5">
                    <summary className="cursor-pointer font-display text-lg">{f.q}</summary>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
